import Lock from "@mui/icons-material/Lock";
import LockOpen from "@mui/icons-material/LockOpen";
import TextFields from "@mui/icons-material/TextFields";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import type { EditorOptions } from "@tiptap/core";
import { useCallback, useRef, useState, useEffect } from "react";
import {
  LinkBubbleMenu,
  MenuButton,
  RichTextEditor,
  RichTextReadOnly,
  TableBubbleMenu,
  insertImages,
  type RichTextEditorRef,
} from "mui-tiptap";
import EditorMenuControls from "./EditorMenuControls";
import useExtensions from "./useExtensions";

const fileListToImageFiles = (fileList: FileList): File[] => {
  // You may want to use a package like attr-accept
  // (https://www.npmjs.com/package/attr-accept) to restrict to certain file
  // types.
  return Array.from(fileList).filter((file) => {
    const mimeType = (file.type || "").toLowerCase();
    return mimeType.startsWith("image/");
  });
}
// Convert a File to a data URL (base64) so the image can be embedded
// directly into the editor HTML. This avoids using `URL.createObjectURL`
// which produces a browser-local blob URL that won't be valid for other
// users or sessions.
const fileToDataUrl = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.onload = () => resolve(String(reader.result));
    reader.readAsDataURL(file);
  });
}
interface EditorProps {
  disableStickyMenuBar?: boolean;
  // Eski Editor'dan gelen prop'lar
  label?: string;
  value?: string;
  onChange?: (value: string, delta?: any, source?: any, editor?: any) => void;
  sx?: any;
  readOnly?: boolean;
  id?: string;
  key?: string;
  error?: boolean;
  helperText?: string;
  showMenuBar?: boolean;
}

const Editor = (props: EditorProps) => {
  const { disableStickyMenuBar, label, value, onChange, sx, readOnly, id, key, error, helperText, showMenuBar = true } = props;
  const rteRef = useRef<RichTextEditorRef>(null);
  const [editorContent, setEditorContent] = useState(value ?? "");
  const [submittedContent, setSubmittedContent] = useState("");
  const [isEditable, setIsEditable] = useState(!readOnly);
  const extensions = useExtensions({ placeholder: "Metninizi buraya yazın...", });

  const handleNewImageFiles = useCallback(
    async (files: File[], insertPosition?: number): Promise<void> => {
      if (!rteRef.current?.editor) {
        return;
      }

      // For the sake of a demo, we don't have a server to upload the files to,
      // Previously we used URL.createObjectURL(file) which creates a
      // browser-local blob URL that is not valid across sessions. Here we
      // convert the images to data URLs (base64) so they are embedded in the
      // saved HTML and will remain visible to other users. Note: this will
      // increase the HTML size—if you prefer, upload files to a server and
      // replace `src` with the returned public URL instead.
      const attributesForImageFiles = await Promise.all(
        files.map(async (file) => ({
          src: await fileToDataUrl(file),
          alt: file.name,
        }))
      );

      insertImages({
        images: attributesForImageFiles,
        editor: rteRef.current.editor,
        position: insertPosition,
      });
    },
    []
  );

  // Allow for dropping images into the editor
  const handleDrop: NonNullable<EditorOptions["editorProps"]["handleDrop"]> =
    useCallback(
      (view, event, _slice, _moved) => {
        if (!(event instanceof DragEvent) || !event.dataTransfer) {
          return false;
        }

        const imageFiles = fileListToImageFiles(event.dataTransfer.files);
        if (imageFiles.length > 0) {
          const insertPosition = view.posAtCoords({
            left: event.clientX,
            top: event.clientY,
          })?.pos;

          handleNewImageFiles(imageFiles, insertPosition);

          // Return true to treat the event as handled. We call preventDefault
          // ourselves for good measure.
          event.preventDefault();
          return true;
        }

        return false;
      },
      [handleNewImageFiles]
    );

  // Allow for pasting images only - everything else pastes as-is
  const handlePaste: NonNullable<EditorOptions["editorProps"]["handlePaste"]> =
    useCallback(
      (_view, event, _slice) => {
        if (!event.clipboardData) {
          return false;
        }

        // Excel'den kopyalanan veriyi kontrol et
        const text = event.clipboardData.getData('text/plain');
        const html = event.clipboardData.getData('text/html');

        // Excel'den kopyalanan veriyi (Excel'e özgü şema içeriyorsa) özel olarak işle
        if (html.includes("urn:schemas-microsoft-com:office:excel")) {
          event.preventDefault();

          if (text && rteRef.current?.editor) {
            // Excel'den gelen HTML'i temiz tablo formatına çevir
            const tableMatch = html.match(/<table[^>]*>[\s\S]*?<\/table>/i);
            if (tableMatch) {
              // HTML tablosundan sadece metin içeriğini çıkar ve temiz tablo oluştur
              const tempDiv = document.createElement('div');
              tempDiv.innerHTML = tableMatch[0];

              const rows = tempDiv.querySelectorAll('tr');
              const tableRows = Array.from(rows).map((row, rowIndex) => {
                const cells = row.querySelectorAll('td, th');
                const cellElements = Array.from(cells).map(cell => {
                  const cellText = cell.textContent?.trim() || '';
                  // Boş hücreler için &nbsp; kullan (TipTap tableCell node'u için gerekli)
                  const displayText = cellText === '' ? '&nbsp;' : cellText;
                  // İlk satır her zaman başlık satırı olarak kabul edilir
                  const tag = rowIndex === 0 ? 'th' : 'td';
                  return `<${tag}>${displayText}</${tag}>`;
                }).join('');
                return `<tr>${cellElements}</tr>`;
              }).join('');

              const tableHtml = `<table><tbody>${tableRows}</tbody></table>`;
              rteRef.current.editor.commands.insertContent(tableHtml);
            } else {
              // HTML tablo yoksa sadece metin olarak yapıştır
              rteRef.current.editor.commands.insertContent(text);
            }
            return true;
          }
        }

        // Sadece görsel dosyalarını kontrol et - diğer her şey olduğu gibi yapışsın

        const pastedImageFiles = fileListToImageFiles(
          event.clipboardData.files
        );
        if (pastedImageFiles.length > 0) {
          // Use our handler which converts files to data URLs and inserts
          // them. Prevent the default paste so the editor doesn't also
          // insert HTML (which in some cases contains blob: URLs).
          handleNewImageFiles(pastedImageFiles);
          event.preventDefault();
          // Return true to mark the paste event as handled.
          return true;
        }

        // We return false here to allow the standard paste-handler to run.
        return false;
      },
      [handleNewImageFiles]
    );


  useEffect(() => {
    const incoming = value ?? "";
    if (rteRef.current?.editor) {
      try {
        const current = rteRef.current.editor.getHTML();
        if (incoming !== current) {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          rteRef.current.editor.commands.setContent(incoming, false);
          setEditorContent(incoming);
        }
      } catch (err) {
        setEditorContent(incoming);
      }
    } else {
      setEditorContent(incoming);
    }
  }, [value]);

  const handleContentChange = useCallback(() => {
    const html = rteRef.current?.editor?.getHTML() ?? "";
    setEditorContent((prev) => {
      if (prev === html) return prev;
      return html;
    });
    if (onChange)
      onChange(html);
  }, [onChange]);

  return (
    <>
      <RichTextEditor
        ref={rteRef}
        extensions={extensions}
        content={editorContent}
        editable={isEditable}
        onUpdate={handleContentChange}
        editorProps={{
          handleDrop: handleDrop,
          handlePaste: handlePaste,
        }}
        renderControls={() => <EditorMenuControls />}
        RichTextFieldProps={{
          // The "outlined" variant is the default (shown here only as
          // example), but can be changed to "standard" to remove the outlined
          // field border from the editor
          variant: "standard",
          MenuBarProps: {
            hide: !showMenuBar,
            disableSticky: disableStickyMenuBar,
          },
          // Below is an example of adding a toggle within the outlined field
          // for showing/hiding the editor menu bar, and a "submit" button for
          // saving/viewing the HTML content
          footer: (
            <Stack
              direction="row"
              spacing={1}
              sx={{
                borderTopStyle: "solid",
                borderTopWidth: 1,
                borderTopColor: (theme) => theme.palette.divider,
                py: 1,
                px: 1.5,
              }}
            >
              {/* <MenuButton
                value="formatting"
                tooltipLabel={
                  showMenuBar ? "Hide formatting" : "Show formatting"
                }
                size="small"
                onClick={() => {
                  setShowMenuBar((currentState) => !currentState);
                }}
                selected={showMenuBar}
                IconComponent={TextFields}
              /> */}
              {/* 
              <MenuButton
                value="formatting"
                tooltipLabel={
                  isEditable
                    ? "Prevent edits (use read-only mode)"
                    : "Allow edits"
                }
                size="small"
                onClick={() => {
                  setIsEditable((currentState) => !currentState);
                }}
                selected={!isEditable}
                IconComponent={isEditable ? Lock : LockOpen}
              /> */}
            </Stack>
          ),
        }}
        sx={{
          // An example of how editor styles can be overridden. In this case,
          // setting where the scroll anchors to when jumping to headings. The
          // scroll margin isn't built in since it will likely vary depending on
          // where the editor itself is rendered (e.g. if there's a sticky nav
          // bar on your site).
          border: "1.5px solid #e0e0e0",
          borderRadius: 1,
          boxShadow: "0 2px 8px 0 rgba(60,60,60,0.06)",
          background: "#fff",
          transition: "box-shadow 0.2s, border-color 0.2s",
          "&:hover": {
            borderColor: "#bdbdbd",
            boxShadow: "0 4px 16px 0 rgba(60,60,60,0.10)",
          },
          "&:focus-within": {
            borderColor: "#1976d2",
            boxShadow: "0 0 0 2px #1976d233",
          },
          "& .ProseMirror": {
            "& h1, & h2, & h3, & h4, & h5, & h6": {
              scrollMarginTop: showMenuBar ? 50 : 0,
            },
          },
          overflow: "auto",
          ...sx, // Dışarıdan gelen sx prop'larını merge et
        }}
      >
        {() => (
          <>
            <LinkBubbleMenu />
            <TableBubbleMenu />
          </>
        )}
      </RichTextEditor>

    </>
  );
}

export default Editor;
